import { useMemo } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

import Spinner from "../../components/Spinner";
import { formatEuro, formatPercent } from "../../lib/format";

export type ShareRow = { id: string; label: string; value: number };

const REST_ID = "__rest__";
const REST_COLOR = "#cbd5e1";

/**
 * Ring mit Liste daneben (Analyse, wie die Preis-Analyse in Beta 29): Anteil je
 * Großhändler oder Hersteller an Mehrkosten oder Ausgaben. Ein Klick filtert
 * die ganze Seite auf diesen Eintrag; ein gewählter Eintrag bleibt voll, die
 * übrigen treten zurück.
 */
export default function ShareDonutCard({
  title,
  rows,
  colorFor,
  isLoading,
  isError,
  emptyText,
  selected = [],
  onSelect,
  topN = 6,
  restLabel,
  showBars = true,
  size = 132,
  stacked = false,
}: {
  title: string;
  rows: readonly ShareRow[];
  colorFor: (id: string) => string;
  isLoading: boolean;
  isError?: boolean;
  emptyText: string;
  selected?: readonly string[];
  onSelect?: (id: string) => void;
  topN?: number;
  restLabel: (count: number) => string;
  showBars?: boolean;
  size?: number;
  /** Ring above the list – for narrow columns, so names are not cut. */
  stacked?: boolean;
}) {
  const { slices, total } = useMemo(() => {
    const sorted = rows.filter((row) => row.value > 0).sort((a, b) => b.value - a.value);
    const total = sorted.reduce((sum, row) => sum + row.value, 0);
    const head = sorted.slice(0, topN);
    const tail = sorted.slice(topN);
    const restValue = tail.reduce((sum, row) => sum + row.value, 0);
    const slices = tail.length
      ? [...head, { id: REST_ID, label: restLabel(tail.length), value: restValue }]
      : head;
    return { slices, total };
  }, [restLabel, rows, topN]);
  const selectedSet = new Set(selected);
  const dimmed = (id: string) => selectedSet.size > 0 && id !== REST_ID && !selectedSet.has(id);
  const color = (id: string) => (id === REST_ID ? REST_COLOR : colorFor(id));
  const max = Math.max(0, ...slices.map((slice) => slice.value));

  return (
    <section className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      {isLoading ? (
        <div className="flex h-36 items-center justify-center">
          <Spinner size="sm" label={`Lade ${title} …`} />
        </div>
      ) : isError ? (
        <p className="mt-3 text-sm text-rose-700">Die Auswertung konnte nicht geladen werden.</p>
      ) : total <= 0 ? (
        <p className="mt-3 text-sm text-slate-600">{emptyText}</p>
      ) : (
        <div className={`mt-3 flex flex-col items-center gap-4 ${stacked ? "" : "sm:flex-row sm:items-center sm:gap-6"}`}>
          <div className="shrink-0" style={{ width: size, height: size }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={size * 0.3}
                  outerRadius={size * 0.5}
                  stroke="#fff"
                  strokeWidth={1}
                  isAnimationActive={false}
                  onClick={(_data, index) => {
                    const slice = slices[index];
                    if (slice && slice.id !== REST_ID) onSelect?.(slice.id);
                  }}
                >
                  {slices.map((slice) => (
                    <Cell
                      key={slice.id}
                      fill={color(slice.id)}
                      opacity={dimmed(slice.id) ? 0.3 : 1}
                      style={{ cursor: onSelect && slice.id !== REST_ID ? "pointer" : "default", outline: "none" }}
                    />
                  ))}
                </Pie>
                <Tooltip
                  cursor={false}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const entry = payload[0];
                    return (
                      <div className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs shadow">
                        <div className="font-medium text-slate-900">{String(entry.name)}</div>
                        <div className="text-slate-600">{formatEuro(Number(entry.value ?? 0))}</div>
                      </div>
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="w-full min-w-0 flex-1 text-sm">
            {slices.map((slice) => {
              const share = total > 0 ? (slice.value / total) * 100 : 0;
              const clickable = Boolean(onSelect) && slice.id !== REST_ID;
              const content = (
                <>
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: color(slice.id) }} />
                    <span className={`truncate ${slice.id === REST_ID ? "text-slate-600" : "text-slate-800"}`}>{slice.label}</span>
                  </span>
                  {showBars ? (
                    <span className="hidden h-1.5 overflow-hidden rounded-full bg-slate-100 sm:block">
                      <span className="block h-full rounded-full" style={{ width: `${max > 0 ? (slice.value / max) * 100 : 0}%`, backgroundColor: color(slice.id), opacity: dimmed(slice.id) ? 0.3 : 1 }} />
                    </span>
                  ) : null}
                  <span className="text-right font-semibold tabular-nums text-slate-900">{formatEuro(slice.value)}</span>
                  <span className="w-14 text-right tabular-nums text-slate-600">{formatPercent(share)}</span>
                </>
              );
              const grid = `grid w-full items-center gap-3 rounded px-1.5 py-1.5 text-left ${showBars ? "grid-cols-[minmax(0,1fr)_auto_auto] sm:grid-cols-[minmax(0,1fr)_6rem_auto_auto]" : "grid-cols-[minmax(0,1fr)_auto_auto]"}`;
              return (
                <li key={slice.id} className="border-t border-slate-100 first:border-t-0">
                  {clickable ? (
                    <button
                      type="button"
                      onClick={() => onSelect?.(slice.id)}
                      aria-pressed={selectedSet.has(slice.id)}
                      title={`Nur ${slice.label} zeigen`}
                      className={`${grid} hover:bg-slate-50 ${selectedSet.has(slice.id) ? "bg-slate-50 font-medium" : ""}`}
                    >
                      {content}
                    </button>
                  ) : (
                    <div className={grid}>{content}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}
